import { DATA_TYPE_VALUES } from "@/constants/dataset";
import {
  DataSchemaType,
  DatasetAttributeSchema,
  DatasetDataType
} from "@/types/dataset";

type ScoringAttribute = {
  id: string;
  paired_key_attribute_id?: string | null;
  position?: number | null;
};

/**
 * Returns true when an attribute's data_type is INT or FLOAT.
 * Used to gate scoring/weighting features that only apply to numeric data.
 *
 * Accepts both `DatasetAttributeSchema` (typed `DataSchemaType`) and
 * `DatasetDetailSchemaResponse` (typed `string`) shapes.
 */
export const isNumericAttribute = (attribute: {
  data_type: DataSchemaType | string;
}): boolean =>
  attribute.data_type === DATA_TYPE_VALUES[DatasetDataType.TYPE_INTEGER] ||
  attribute.data_type === DATA_TYPE_VALUES[DatasetDataType.TYPE_FLOAT];

export const getDatasetAttributeScoringId = (
  attribute: ScoringAttribute
): string => attribute.paired_key_attribute_id || attribute.id;
export const getPrimaryPoiAttribute = (
  attributes: readonly ScoringAttribute[]
): ScoringAttribute | undefined =>
  attributes.reduce<ScoringAttribute | undefined>((primary, attribute) => {
    if (!primary) return attribute;

    const primaryPosition =
      typeof primary.position === "number"
        ? primary.position
        : Number.POSITIVE_INFINITY;

    const attributePosition =
      typeof attribute.position === "number"
        ? attribute.position
        : Number.POSITIVE_INFINITY;

    return attributePosition < primaryPosition ? attribute : primary;
  }, undefined);

/**
 * Filters dataset categories to return only valid numeric attributes.
 * Valid attributes are those with INT or FLOAT data types, excluding the "id" attribute.
 *
 * @param categories - Array of dataset attribute schemas to filter
 * @returns Array of valid numeric attributes
 *
 * @example
 * const validAttrs = getValidNumericAttributes(dataset.categories);
 * console.log(validAttrs.length); // Number of valid attributes for weighting
 */
export const getValidNumericAttributes = (
  categories: DatasetAttributeSchema[]
): DatasetAttributeSchema[] => {
  return categories.filter(
    (category) =>
      isNumericAttribute(category) && category.attribute_name !== "id"
  );
};

/**
 * Gets the count of valid numeric attributes in a dataset.
 * This is a convenience wrapper around getValidNumericAttributes that returns only the count.
 *
 * @param categories - Array of dataset attribute schemas
 * @returns Number of valid numeric attributes
 *
 * @example
 * const count = getValidNumericAttributeCount(dataset.categories);
 * console.log(count); // e.g., 5
 */
export const getValidNumericAttributeCount = (
  categories: DatasetAttributeSchema[]
): number => {
  return getValidNumericAttributes(categories).length;
};
